import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import type { Profile } from './types/api'
import LoginPage from './pages/LoginPage'
import LinkSignInPage from './pages/LinkSignInPage'
import SignupPage from './pages/SignupPage'
import GitHubSetupPage from './pages/GitHubSetupPage'
import SuperAdminPage from './pages/SuperAdminPage'
import ConnectPage from './pages/ConnectPage'
import ScansPage from './pages/ScansPage'
import MonitorPage from './pages/MonitorPage'
import FindingsPage from './pages/FindingsPage'
import ExecutiveReportPage from './pages/ExecutiveReportPage'
import ProductReportPage from './pages/ProductReportPage'
import AdminPage from './pages/AdminPage'
import SettingsPage, { SettingsRedirect } from './pages/SettingsPage'
import { Spinner } from './components/shared/Spinner'

const GraphPage = lazy(() => import('./pages/GraphPage'))
// Lazy loaded for the same reason GraphPage is: recharts is only needed here (DEC-116).
const TrendsPage = lazy(() => import('./pages/TrendsPage'))

// ---------------------------------------------------------------------------
// Route guard
// ---------------------------------------------------------------------------

/**
 * Wraps any route that requires authentication (rewritten by DEC-141).
 *
 * The session is an `HttpOnly` cookie, so this can no longer answer
 * synchronously from `localStorage` the way it used to. It reads the resolved
 * state from `AuthProvider`, which means a `resolving` phase.
 *
 * **The spinner is not optional.** Rendering the anonymous branch while
 * `/auth/me` is still in flight would flash the login screen at a signed-in
 * user on every reload, and redirect them away from the page they asked for.
 */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const { status } = useAuth()

  if (status === 'resolving') {
    return (
      <div className="min-h-screen bg-ground flex items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (status === 'anonymous') {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  return <>{children}</>
}

/**
 * Wraps a route that requires a specific profile (§30.3, DEC-085, DEC-179).
 *
 * Two callers pass every gate: one with no `profile` (the admin-secret back
 * door) and the `admin` profile itself. Under DEC-085 admin was a peer of the
 * three report lenses, so a workspace admin could open Settings and the admin
 * lens and nothing else — and in a one-person workspace that locked the owner
 * out of every report she produced. DEC-179 makes admin a superset.
 *
 * This is a UX convenience only; the backend enforces the real gate on every
 * request (`require_profile`, which carries the same rule).
 */
function RequireProfile({ profile, children }: { profile: Profile; children: React.ReactNode }) {
  const { claims } = useAuth()
  const mine = claims?.profile ?? null

  if (mine !== null && mine !== 'admin' && mine !== profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ground text-ink px-6">
        <div className="text-center max-w-sm">
          <div className="font-semibold text-[15px] mb-1">Not available for your profile</div>
          {/*
            §38.7: naming only the REQUIRED profile told the reader nothing about
            what to ask for or whom to ask. Their own profile and who sets it are
            the two facts that make this message actionable.
          */}
          <div className="text-[13px] text-muted leading-relaxed">
            This page requires the {profile} profile. Yours is {mine}. Profiles are set
            by your workspace admin.
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      {/* The emailed link target (DEC-140). Public by necessity: the visitor is
          not signed in yet, which is the entire point of the page. */}
      <Route path="/link" element={<LinkSignInPage />} />
      {/* DEC-202: "Create a workspace". Public, like /link: the emailed or Google-issued
          token is the proof of the address, spent only when the button is pressed. */}
      <Route path="/signup" element={<SignupPage />} />
      {/* DEC-200: where GitHub sends a person after installing the App. Public: a new
          Marketplace customer is not signed in yet. */}
      <Route path="/github/setup" element={<GitHubSetupPage />} />

      {/* Protected */}
      {/* DEC-191: the Scans dashboard is home. `/connect` stays the New scan form, so
          its deep links (the GitHub OAuth return, `?mode=upload`) keep working. */}
      <Route
        path="/"
        element={<Navigate to="/scans" replace />}
      />
      <Route
        path="/scans"
        element={<RequireAuth><ScansPage /></RequireAuth>}
      />
      <Route
        path="/connect"
        element={<RequireAuth><ConnectPage /></RequireAuth>}
      />
      {/* Workspace-scoped, available to any authenticated profile (UI_SDD §R.9). */}
      <Route
        path="/trends"
        element={
          <RequireAuth>
            <Suspense fallback={<div className="min-h-screen bg-ground flex items-center justify-center"><Spinner /></div>}>
              <TrendsPage />
            </Suspense>
          </RequireAuth>
        }
      />
      {/* DEC-198: Settings is reached from the user menu, one URL per section. The page
          gates the workspace sections to admins (the backend enforces it on every call).
          `/settings` was the single admin page; it, and a bare group, redirect with
          their query kept, because the GitHub sign-in may still return there. */}
      <Route
        path="/settings/:group/:section"
        element={<RequireAuth><SettingsPage /></RequireAuth>}
      />
      <Route path="/settings/:group?" element={<RequireAuth><SettingsRedirect /></RequireAuth>} />
      {/* DEC-204: Super Admin. The page sends anyone else to Scans; the API answers 404. */}
      <Route path="/super-admin/:section?" element={<RequireAuth><SuperAdminPage /></RequireAuth>} />
      <Route
        path="/scans/:scanId/monitor"
        element={<RequireAuth><MonitorPage /></RequireAuth>}
      />
      <Route
        path="/scans/:scanId/findings"
        element={<RequireAuth><RequireProfile profile="engineering"><FindingsPage /></RequireProfile></RequireAuth>}
      />
      <Route
        path="/scans/:scanId/graph"
        element={
          <RequireAuth>
            <Suspense fallback={<div className="min-h-screen bg-fill-subtle flex items-center justify-center"><Spinner /></div>}>
              <GraphPage />
            </Suspense>
          </RequireAuth>
        }
      />
      <Route
        path="/scans/:scanId/executive-report"
        element={<RequireAuth><RequireProfile profile="executive"><ExecutiveReportPage /></RequireProfile></RequireAuth>}
      />
      <Route
        path="/scans/:scanId/product-report"
        element={<RequireAuth><RequireProfile profile="product_gtm"><ProductReportPage /></RequireProfile></RequireAuth>}
      />
      <Route
        path="/scans/:scanId/admin"
        element={<RequireAuth><RequireProfile profile="admin"><AdminPage /></RequireProfile></RequireAuth>}
      />
    </Routes>
  )
}
